import { useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MediaPicker } from "@/components/admin/MediaPicker";
import { UploadCloud, Library, Wand2, Sparkles, Loader2 } from "lucide-react";

// Reusable admin image field: drag & drop upload (auto-resized server-side),
// media library pick, AI generation, and a plain URL fallback. Value is a string URL.
export const AdminImageInput = ({ value, onChange, testid }) => {
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [gen, setGen] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("alt", file.name.replace(/\.[^.]+$/, ""));
      const { data } = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded & optimized");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed. Previous image kept."); }
    finally { setUploading(false); }
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe the image first"); return; }
    setGen(true);
    try {
      const { data } = await api.post("/admin/ai/suggest-image", { prompt, alt: prompt }, { timeout: 90000 });
      onChange(data.url);
      toast.success("AI image generated");
      setGenOpen(false); setPrompt("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Image generation failed"); }
    finally { setGen(false); }
  };

  return (
    <div className="mt-1.5 space-y-2" data-testid={testid}>
      {value ? (
        <div
          className="relative w-full max-w-xs overflow-hidden rounded-md border border-border"
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
          data-testid={testid ? `${testid}-preview-wrap` : undefined}
        >
          <img src={value} alt="preview" className="h-32 w-full object-cover" data-testid={testid ? `${testid}-preview` : undefined} />
          {(drag || uploading) && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/60 text-xs font-semibold uppercase tracking-wide text-white">{uploading ? "Uploading…" : "Drop to replace"}</div>
          )}
          <div className="absolute right-2 top-2 flex gap-1.5">
            <button type="button" onClick={() => inputRef.current?.click()} className="rounded bg-black/60 px-2 py-1 text-xs font-semibold text-white hover:bg-black/80" data-testid={testid ? `${testid}-replace` : undefined}>Replace</button>
            <button type="button" onClick={() => onChange("")} className="rounded bg-black/60 px-2 py-1 text-xs font-semibold text-white hover:bg-black/80" data-testid={testid ? `${testid}-remove` : undefined}>Remove</button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
          onClick={() => inputRef.current?.click()}
          data-testid={testid ? `${testid}-dropzone` : undefined}
          className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed p-6 text-center transition-colors ${drag ? "border-[hsl(var(--brand-red))] bg-brand/5" : "border-border hover:border-muted-foreground/40"}`}
        >
          <UploadCloud className="h-7 w-7 text-brand" />
          <p className="text-sm text-muted-foreground">{uploading ? "Uploading…" : "Drag & drop or click to upload"}</p>
          <p className="text-[11px] text-muted-foreground">Large images are auto-resized &amp; optimized</p>
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid={testid ? `${testid}-input` : undefined} />

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => setPickerOpen(true)} className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase hover:bg-accent transition-colors"><Library className="h-4 w-4" /> Library</button>
        <Popover open={genOpen} onOpenChange={setGenOpen}>
          <PopoverTrigger asChild>
            <button type="button" data-testid={testid ? `${testid}-ai` : undefined} className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-xs font-semibold uppercase text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors"><Wand2 className="h-4 w-4" /> Generate with AI</button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 z-[90]">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Describe the image</Label>
            <Textarea rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. athletes training in a modern gym, dramatic lighting" className="mt-1.5" />
            <button type="button" onClick={generate} disabled={gen} className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-2 text-xs font-semibold uppercase text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors disabled:opacity-60">
              {gen ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</> : <><Sparkles className="h-4 w-4" /> Generate</>}
            </button>
          </PopoverContent>
        </Popover>
      </div>

      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://… or /api/media/file/…" />
      <MediaPicker open={pickerOpen} onOpenChange={setPickerOpen} onSelect={(m) => onChange(m.url)} />
    </div>
  );
};
